import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const FEED_LABELS = {
  cve: "CVE / vulnerabilities",
  gdacs: "GDACS disasters",
  sanctions_lists: "Sanctions lists",
  gdelt: "GDELT news",
  opensanctions: "OpenSanctions",
  comtrade: "UN Comtrade",
};

/**
 * Compact "all systems operational" indicator for the Navbar, fed by the
 * /api/status endpoint (database ping + data-feed circuit-breaker state).
 * Polls every 60s. Purely informational — never blocks anything.
 */
export const SystemStatus = () => {
  const [s, setS] = useState(null);
  const [voice, setVoice] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const poll = () => api.get("/status").then(({ data }) => active && setS(data)).catch(() => {});
    const pollVoice = () => api.get("/voice/usage").then(({ data }) => active && setVoice(data)).catch(() => {});
    poll();
    pollVoice();
    const id = setInterval(poll, 60_000);
    const vid = setInterval(pollVoice, 300_000);
    return () => { active = false; clearInterval(id); clearInterval(vid); };
  }, []);

  if (!s) return null;
  const ok = s.operational;

  return (
    <div className="relative hidden sm:block"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="system-status"
        aria-label="System status"
        className="flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[11px] font-medium text-white/70 transition-colors hover:text-white"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-amber-400"} ${ok ? "" : "animate-pulse"}`} />
        {ok ? "All systems operational" : "Partial degradation"}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-white/10 bg-[#0b0b0c] p-3 text-xs shadow-xl"
          data-testid="system-status-panel">
          <div className="flex items-center justify-between text-white/70">
            <span>Database</span>
            <span className={s.database === "ok" ? "text-emerald-400" : "text-red-400"}>{s.database}</span>
          </div>
          <div className="mt-1.5 text-[10px] uppercase tracking-wide text-white/35">Data feeds</div>
          {Object.entries(s.feeds || {}).map(([name, st]) => (
            <div key={name} className="mt-1 flex items-center justify-between text-white/70">
              <span>{FEED_LABELS[name] || name.replace(/_/g, " ")}</span>
              <span className={st === "open" ? "text-amber-400" : "text-emerald-400"}>
                {st === "open" ? "degraded" : "ok"}
              </span>
            </div>
          ))}
          {voice && voice.available && (
            <>
              <div className="mt-1.5 text-[10px] uppercase tracking-wide text-white/35">Voice narration</div>
              <div className="mt-1 flex items-center justify-between text-white/70" data-testid="voice-credits">
                <span>Credits remaining</span>
                <span className={voice.remaining <= 0 ? "text-red-400" : voice.percent_used >= 90 ? "text-amber-400" : "text-emerald-400"}>
                  {voice.remaining.toLocaleString()} / {voice.limit.toLocaleString()}
                </span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
                <div className={`h-full rounded-full ${voice.remaining <= 0 ? "bg-red-400" : voice.percent_used >= 90 ? "bg-amber-400" : "bg-emerald-400"}`}
                  style={{ width: `${Math.min(100, voice.percent_used)}%` }} />
              </div>
              {voice.remaining <= 0 && (
                <p className="mt-1 text-[10px] text-red-300" data-testid="voice-credits-warning">
                  Narration paused — monthly voice quota reached.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
